import { Helmet } from 'react-helmet';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Link } from 'react-router-dom';

const Privacy = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | InvizHer - Programming Tutorials & Resources</title>
        <meta name="description" content="Learn about InvizHer's privacy practices. We're committed to protecting your data while providing high-quality programming tutorials, projects, and development resources." />
        <meta name="keywords" content="privacy policy, programming blog privacy, developer privacy, coding tutorials privacy, InvizHer privacy, programming resources privacy" />
        <link rel="canonical" href="https://invizher.com/privacy" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-28 pb-16" role="main">
          {/* Hero Section */}
          <section className="container mx-auto px-4 mb-12">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="text-4xl lg:text-5xl font-bold mb-6 gradient-text">Privacy Policy</h1>
              <p className="text-muted-foreground text-lg mb-8">
                Your privacy matters to us. Learn about how we collect, use, and protect your information while providing high-quality programming content.
              </p>
              <div className="flex justify-center">
                <span className="text-sm text-muted-foreground">Last updated: December 29, 2024</span>
              </div>
            </div>
          </section>

          {/* Main Content */}
          <div className="container mx-auto px-4">
            <div className="lg:grid lg:grid-cols-4 lg:gap-8">
              {/* Sidebar with Table of Contents */}
              <div className="lg:col-span-1 mb-8 lg:mb-0">
                <div className="glass-morphism rounded-2xl p-6 lg:sticky lg:top-28">
                  <h2 className="text-xl font-bold mb-4 gradient-text">Quick Navigation</h2>
                  <ul className="space-y-3">
                    <li><a href="#introduction" className="text-muted-foreground hover:text-primary transition-colors">Introduction</a></li>
                    <li><a href="#information-collection" className="text-muted-foreground hover:text-primary transition-colors">Information Collection</a></li>
                    <li><a href="#use-of-information" className="text-muted-foreground hover:text-primary transition-colors">Use of Information</a></li>
                    <li><a href="#data-security" className="text-muted-foreground hover:text-primary transition-colors">Data Security</a></li>
                    <li><a href="#cookies" className="text-muted-foreground hover:text-primary transition-colors">Cookies Policy</a></li>
                    <li><a href="#third-party" className="text-muted-foreground hover:text-primary transition-colors">Third-Party Services</a></li>
                    <li><a href="#user-rights" className="text-muted-foreground hover:text-primary transition-colors">Your Rights</a></li>
                    <li><a href="#updates" className="text-muted-foreground hover:text-primary transition-colors">Policy Updates</a></li>
                  </ul>
                </div>
              </div>

              {/* Main Content Sections */}
              <div className="lg:col-span-3">
                <div className="glass-morphism rounded-2xl p-6 sm:p-8 space-y-12">
                  {/* Introduction */}
                  <section id="introduction">
                    <h2 className="text-2xl font-bold mb-6 gradient-text">Introduction</h2>
                    <div className="space-y-4 text-muted-foreground">
                      <p>Welcome to InvizHer's Privacy Policy. This document outlines our commitment to protecting your privacy and explains how we handle information when you use our programming blog and educational resources.</p>
                      <p>As a platform dedicated to sharing programming knowledge and development resources, we understand the importance of maintaining the trust of our community. This policy applies to all content, features, and services available through invizher.com.</p>
                    </div>
                  </section>

                  {/* Information Collection */}
                  <section id="information-collection">
                    <h2 className="text-2xl font-bold mb-6 gradient-text">Information Collection</h2>
                    <div className="space-y-6 text-muted-foreground">
                      <div>
                        <h3 className="text-xl font-semibold text-foreground mb-4">Automatically Collected Information</h3>
                        <div className="bg-card/50 rounded-xl p-6">
                          <ul className="space-y-3">
                            <li className="flex items-start gap-3">
                              <span className="text-green-500 mt-1">✓</span>
                              <span>Device information (browser type, operating system)</span>
                            </li>
                            <li className="flex items-start gap-3">
                              <span className="text-green-500 mt-1">✓</span>
                              <span>IP address and general location data</span>
                            </li>
                            <li className="flex items-start gap-3">
                              <span className="text-green-500 mt-1">✓</span>
                              <span>Pages visited and time spent on pages</span>
                            </li>
                            <li className="flex items-start gap-3">
                              <span className="text-green-500 mt-1">✓</span>
                              <span>Referral sources and site navigation patterns</span>
                            </li>
                          </ul>
                        </div>
                      </div>

                      <div>
                        <h3 className="text-xl font-semibold text-foreground mb-4">User-Provided Information</h3>
                        <div className="bg-card/50 rounded-xl p-6">
                          <ul className="space-y-3">
                            <li className="flex items-start gap-3">
                              <span className="text-green-500 mt-1">✓</span>
                              <span>Comments and feedback on blog posts</span>
                            </li>
                            <li className="flex items-start gap-3">
                              <span className="text-green-500 mt-1">✓</span>
                              <span>Contact form submissions</span>
                            </li>
                            <li className="flex items-start gap-3">
                              <span className="text-green-500 mt-1">✓</span>
                              <span>Newsletter subscriptions</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Use of Information */}
                  <section id="use-of-information">
                    <h2 className="text-2xl font-bold mb-6 gradient-text">Use of Information</h2>
                    <div className="grid md:grid-cols-2 gap-6 text-muted-foreground">
                      <div className="glass-morphism p-6 rounded-xl">
                        <h3 className="text-lg font-semibold text-foreground mb-4">Primary Uses</h3>
                        <ul className="space-y-3">
                          <li className="flex items-start gap-3">
                            <span className="text-primary mt-1">→</span>
                            <span>Improving website content and user experience</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <span className="text-primary mt-1">→</span>
                            <span>Analyzing site performance and traffic patterns</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <span className="text-primary mt-1">→</span>
                            <span>Personalizing content recommendations</span>
                          </li>
                        </ul>
                      </div>
                      <div className="glass-morphism p-6 rounded-xl">
                        <h3 className="text-lg font-semibold text-foreground mb-4">Secondary Uses</h3>
                        <ul className="space-y-3">
                          <li className="flex items-start gap-3">
                            <span className="text-primary mt-1">→</span>
                            <span>Communication about new content and features</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <span className="text-primary mt-1">→</span>
                            <span>Technical support and user assistance</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <span className="text-primary mt-1">→</span>
                            <span>Service improvements and optimization</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </section>

                  {/* Data Security */}
                  <section id="data-security">
                    <h2 className="text-2xl font-bold mb-6 gradient-text">Data Security</h2>
                    <div className="space-y-6 text-muted-foreground">
                      <div className="bg-blue-50 dark:bg-blue-950/30 border-l-4 border-primary p-6 rounded-r-xl">
                        <h3 className="text-lg font-semibold text-foreground mb-2">Our Security Commitment</h3>
                        <p>We implement industry-standard security measures to protect your data from unauthorized access, disclosure, alteration, and destruction.</p>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-card/50 p-6 rounded-xl">
                          <h4 className="font-semibold text-foreground mb-3">Technical Measures</h4>
                          <ul className="space-y-2">
                            <li>• SSL/TLS encryption</li>
                            <li>• Regular security audits</li>
                            <li>• Secure data storage</li>
                            <li>• Firewall protection</li>
                          </ul>
                        </div>
                        <div className="bg-card/50 p-6 rounded-xl">
                          <h4 className="font-semibold text-foreground mb-3">Organizational Measures</h4>
                          <ul className="space-y-2">
                            <li>• Limited staff access</li>
                            <li>• Employee training</li>
                            <li>• Regular policy reviews</li>
                            <li>• Incident response plan</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Cookies Policy */}
                  <section id="cookies">
                    <h2 className="text-2xl font-bold mb-6 gradient-text">Cookies Policy</h2>
                    <div className="space-y-6 text-muted-foreground">
                      <p>We use cookies and similar tracking technologies to enhance your browsing experience and analyze site usage. Types of cookies we use:</p>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="glass-morphism p-6 rounded-xl">
                          <h3 className="text-lg font-semibold text-foreground mb-4">Essential Cookies</h3>
                          <p>Required for basic site functionality and security. Cannot be disabled.</p>
                        </div>
                        <div className="glass-morphism p-6 rounded-xl">
                          <h3 className="text-lg font-semibold text-foreground mb-4">Analytics Cookies</h3>
                          <p>Help us understand how visitors interact with our content.</p>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Third-Party Services */}
                  <section id="third-party">
                    <h2 className="text-2xl font-bold mb-6 gradient-text">Third-Party Services</h2>
                    <div className="space-y-4 text-muted-foreground">
                      <p>We may use third-party services for:</p>
                      <div className="bg-card/50 rounded-xl p-6">
                        <ul className="space-y-3">
                          <li className="flex items-start gap-3">
                            <span className="text-primary mt-1">🔗</span>
                            <span>Analytics (Google Analytics)</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <span className="text-primary mt-1">🔗</span>
                            <span>Content delivery (CDN services)</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <span className="text-primary mt-1">🔗</span>
                            <span>Newsletter services (Mailchimp)</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </section>

                  {/* Your Rights */}
                  <section id="user-rights">
                    <h2 className="text-2xl font-bold mb-6 gradient-text">Your Rights</h2>
                    <div className="glass-morphism p-6 rounded-xl text-muted-foreground">
                      <ul className="space-y-4">
                        <li className="flex items-start gap-3">
                          <span className="text-purple-500 mt-1">🛡️</span>
                          <span>Right to access your personal data</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className="text-purple-500 mt-1">🛡️</span>
                          <span>Right to request data correction or deletion</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className="text-purple-500 mt-1">🛡️</span>
                          <span>Right to withdraw consent</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className="text-purple-500 mt-1">🛡️</span>
                          <span>Right to data portability</span>
                        </li>
                      </ul>
                    </div>
                  </section>

                  {/* Policy Updates */}
                  <section id="updates">
                    <h2 className="text-2xl font-bold mb-6 gradient-text">Policy Updates</h2>
                    <div className="space-y-4 text-muted-foreground">
                      <p>We reserve the right to update this privacy policy at any time. When we do, we will:</p>
                      <ul className="space-y-3">
                        <li>• Post the updated policy on this page</li>
                        <li>• Update the "Last updated" date at the top of this policy</li>
                        <li>• Notify users of significant changes via email or site notification</li>
                      </ul>
                    </div>
                  </section>
                </div>

                {/* Contact Section */}
                <section className="mt-12 text-center">
                  <div className="glass-morphism rounded-2xl p-8">
                    <h2 className="text-2xl font-bold mb-4">Still Have Questions?</h2>
                    <p className="text-muted-foreground mb-8">Can't find what you're looking for? Feel free to reach out to us directly.</p>
                    <Link to="/contact" className="inline-block px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-full hover:opacity-90 transition-opacity">
                      Contact Us
                    </Link>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Privacy;
